/**
 * Canlı kamera akışı: getUserMedia ile video akışı açar, LiveClassifier ile
 * periyodik sınıflandırma yapar, netlik/ışık kapılarını uygular ve tüm
 * kapılardan üst üste LIVE_REQUIRED_CONSECUTIVE kez geçen kareyi otomatik
 * çeker. `evaluateFrame` SAF fonksiyondur (DOM yazmaz, zamanlayıcı kurmaz);
 * `startLiveCapture` bunu bir setInterval döngüsü içinde çağıran DOM/kamera
 * yan-etkili katmandır.
 */
import type { PhotoSlotId } from "../config";
import {
  LIVE_MIN_CONFIDENCE,
  LIVE_CLASSIFY_INTERVAL_MS,
  LIVE_REQUIRED_CONSECUTIVE,
  SHARPNESS_MIN_VARIANCE,
  LOW_LIGHT_MIN_LUMA,
  TARGET_MAX_EDGE_PX,
  PHOTO_STEPS,
} from "../config";
import type { LiveClassifier, LiveClass } from "./liveClassifier";
import { loadLiveClassifier, LiveClassifierError } from "./liveClassifier";
import type { PreparedImage } from "./imagePrep";
import { prepareFromDrawable } from "./imagePrep";
import { laplacianVariance, averageLuma } from "./imageQuality";

/** Otomatik çekimin neden yapılmadığı, ya da yapılabilir olduğu. Her değer bir
 * i18n anahtarına eşlenir: live_hint_REASON (bkz. Görev 6). */
export type LiveGateReason = "wrong_angle" | "low_confidence" | "blurry" | "dark" | "ready";

export interface LiveFrameEvaluation {
  reason: LiveGateReason;
  className: LiveClass;
  confidence: number;
  sharpness: number;
  luma: number;
  /** Üst üste kaçıncı "ready" karesi. 0 = bu kare ready değil. */
  readyStreak: number;
}

/** SAF fonksiyon: DOM yazmaz, zamanlayıcı kurmaz. Kapı sırası SABİTTİR:
 * sınıf, güven, netlik, ışık. sanity testinin ölçtüğü sözleşme budur. */
export function evaluateFrame(input: {
  expected: PhotoSlotId;
  classification: { className: LiveClass; confidence: number };
  sharpness: number;
  luma: number;
  previousStreak: number;
}): LiveFrameEvaluation {
  const { expected, classification, sharpness, luma, previousStreak } = input;
  const { className, confidence } = classification;

  // Üst/alt oklüzal ayrımı modelin en zayıf olduğu yer (eğitim verisi az —
  // accuracy_report.json per_class support 3-5). Kullanıcı zaten hangi slotu
  // doldurduğunu biliyor; modelin asıl işi "bu gerçekten oklüzal bir görüntü
  // mü, net mi, aydınlık mı" olmalı — üst/alt'ı ayırt etmek değil. Onur'un
  // gerçek cihaz testinde bulundu: alt çeneyi çekerken model "upper_occlusal"
  // diyordu ve tam eşleşme şartı bunu asla geçirmiyordu. Bu yüzden oklüzal
  // slotlarda İKİ oklüzal sınıftan biri de kabul edilir; front_smile'da tam
  // eşleşme KORUNUR (o sınıfta model isabeti zaten yüksek).
  const isOcclusalSlot = expected === "upper_occlusal" || expected === "lower_occlusal";
  const isOcclusalClass = className === "upper_occlusal" || className === "lower_occlusal";
  const classMatches = className === expected || (isOcclusalSlot && isOcclusalClass);

  let reason: LiveGateReason;
  if (!classMatches) {
    reason = "wrong_angle";
  } else if (confidence < LIVE_MIN_CONFIDENCE) {
    reason = "low_confidence";
  } else if (sharpness < SHARPNESS_MIN_VARIANCE) {
    reason = "blurry";
  } else if (luma < LOW_LIGHT_MIN_LUMA) {
    reason = "dark";
  } else {
    reason = "ready";
  }

  const readyStreak = reason === "ready" ? previousStreak + 1 : 0;

  return { reason, className, confidence, sharpness, luma, readyStreak };
}

export type LiveCaptureErrorKind =
  | "insecure_context"
  | "permission_denied"
  | "no_camera"
  | "model_unavailable"
  | "unknown";

export class LiveCaptureError extends Error {
  readonly kind: LiveCaptureErrorKind;
  constructor(kind: LiveCaptureErrorKind, message: string) {
    super(message);
    this.name = "LiveCaptureError";
    this.kind = kind;
  }
}

export interface LiveCaptureCallbacks {
  /** Video akışı GERÇEKTEN bağlandığında bir kez çağrılır: srcObject atandı,
   * play() başarıyla çözüldü ve ilk kare hazır. Görev 6 bunu liveStreamAttached
   * bayrağına yazar; emniyet kontrolü yalnız bu bayrak true iken çalışır.
   * Bayraksız kontrol, ilk açılış penceresinde (srcObject henüz null) canlı
   * oturumu daha başlamadan kapatıyordu — bilinen bir hata sınıfı, tekrar etme. */
  onStreamReady(): void;
  /** Her sınıflandırma turunda çağrılır (yaklaşık LIVE_CLASSIFY_INTERVAL_MS). */
  onEvaluation(evaluation: LiveFrameEvaluation): void;
  /** readyStreak sayacı LIVE_REQUIRED_CONSECUTIVE değerine ulaştığında BİR KEZ
   * çağrılır; sonrasında oturum kendini durdurur, stop() ayrıca gerekmez. */
  onCapture(prepared: PreparedImage, evaluation: LiveFrameEvaluation): void;
  onError(error: LiveCaptureError): void;
}

export interface LiveCaptureHandle {
  /** Döngüyü durdurur, MediaStream track listesini kapatır, classifier kapatır.
   * Çok kez çağrılabilir (idempotent). */
  stop(): void;
}

function stoppedHandle(): LiveCaptureHandle {
  return { stop() {} };
}

/** Video karesini yatayda çevrilmiş bir canvas'a çizer — ön kamera çekiminde
 * ekrandaki ayna efektiyle eşleşen bir kare üretmek için (bkz. çağıran yer). */
function mirrorToCanvas(video: HTMLVideoElement, width: number, height: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d")!;
  ctx.translate(width, 0);
  ctx.scale(-1, 1);
  ctx.drawImage(video, 0, 0, width, height);
  return canvas;
}

/** `err instanceof Error` yerine duck-typing — bazı motorlarda getUserMedia
 * DOMException/Error mirasçısı olmayan bir nesneyle reddedebilir; instanceof
 * kapısı böyle durumlarda hata haritalamasını sessizce "unknown"a düşürür. */
function errorName(err: unknown): string {
  const name = (err as { name?: unknown } | null)?.name;
  return typeof name === "string" ? name : "";
}

/** Kamerayı açar, modeli yükler ve döngüyü başlatır. Herhangi bir adım
 * başarısız olursa onError çağrılır ve dönen handle zaten durdurulmuş olur:
 * çağıran taraf dosya-seçiciye düşer. */
export async function startLiveCapture(args: {
  slotId: PhotoSlotId;
  video: HTMLVideoElement;
  callbacks: LiveCaptureCallbacks;
  classifierFactory?: () => Promise<LiveClassifier>;
  /** Ön/arka kamera zorlaması. Verilmezse PHOTO_STEPS'teki slot varsayılanı
   * kullanılır — kullanıcının canlı overlay'de kamera değiştirme butonuyla
   * geçersiz kılabilmesi için var. */
  facingMode?: "user" | "environment";
  /** Bkz. iptal yarışı sözleşmesi aşağıda. Verilmezse () => false sayılır. */
  isCancelled?: () => boolean;
}): Promise<LiveCaptureHandle> {
  const { slotId, video, callbacks, classifierFactory, isCancelled = () => false } = args;

  let stream: MediaStream | undefined;
  let classifier: LiveClassifier | undefined;
  let intervalId: ReturnType<typeof setInterval> | undefined;
  let stopped = false;

  function cleanup(): void {
    if (intervalId !== undefined) {
      clearInterval(intervalId);
      intervalId = undefined;
    }
    if (stream) {
      for (const track of stream.getTracks()) track.stop();
      stream = undefined;
    }
    if (classifier) {
      classifier.close();
      classifier = undefined;
    }
    // Aksi halde stop()/iptal sonrası video elementi son (donmuş) kareyi
    // tutmaya devam eder ve Görev 6'nın srcObject'e bakan emniyet kontrolü
    // "akış hâlâ canlı" sanabilir.
    video.srcObject = null;
  }

  function stop(): void {
    if (stopped) return;
    stopped = true;
    cleanup();
  }

  // 1. isSecureContext kontrolü.
  if (!window.isSecureContext) {
    callbacks.onError(new LiveCaptureError("insecure_context", "Kamera erişimi yalnız güvenli (HTTPS) bağlamda mümkün."));
    return stoppedHandle();
  }

  if (isCancelled()) {
    return stoppedHandle();
  }

  // 2. getUserMedia. facingMode varsayılanı PHOTO_STEPS'teki tek kaynaktan
  // türetilir — elle kodlanmış eşleme dosya-seçici yolundan sessizce
  // ıraksayabilir. `args.facingMode` verilmişse (kullanıcı kamera değiştirme
  // butonuna bastıysa) o kazanır.
  const capture = args.facingMode ?? PHOTO_STEPS.find((s) => s.id === slotId)?.capture ?? "environment";
  try {
    stream = await navigator.mediaDevices.getUserMedia({
      video: { facingMode: capture, width: { ideal: 1280 } },
      audio: false,
    });
  } catch (err) {
    const name = errorName(err);
    const kind: LiveCaptureErrorKind =
      name === "NotAllowedError"
        ? "permission_denied"
        : name === "NotFoundError" || name === "OverconstrainedError"
          ? "no_camera"
          : "unknown";
    callbacks.onError(new LiveCaptureError(kind, `Kamera akışı açılamadı: ${err instanceof Error ? err.message : String(err)}`));
    return stoppedHandle();
  }

  if (isCancelled()) {
    for (const track of stream.getTracks()) track.stop();
    return stoppedHandle();
  }

  // 3. Sınıflandırıcı.
  try {
    classifier = classifierFactory ? await classifierFactory() : await loadLiveClassifier();
  } catch (err) {
    for (const track of stream.getTracks()) track.stop();
    const message = err instanceof LiveClassifierError ? err.message : err instanceof Error ? err.message : String(err);
    callbacks.onError(new LiveCaptureError("model_unavailable", message));
    return stoppedHandle();
  }

  if (isCancelled()) {
    for (const track of stream.getTracks()) track.stop();
    classifier.close();
    return stoppedHandle();
  }

  // 4. Video akışını bağla.
  video.srcObject = stream;
  try {
    await video.play();
  } catch (err) {
    for (const track of stream.getTracks()) track.stop();
    classifier.close();
    callbacks.onError(new LiveCaptureError("unknown", `Video oynatılamadı: ${err instanceof Error ? err.message : String(err)}`));
    return stoppedHandle();
  }

  if (isCancelled()) {
    for (const track of stream.getTracks()) track.stop();
    classifier.close();
    video.srcObject = null;
    return stoppedHandle();
  }

  // 5. Sınıflandırma döngüsü. Canvas kontrolü onStreamReady'den ÖNCE yapılır —
  // aksi halde tüketici "akış bağlandı" bilgisini alır, sonra hemen hata görür.
  let readyStreak = 0;
  const analysisCanvas = document.createElement("canvas");
  const analysisCtx = analysisCanvas.getContext("2d");
  if (!analysisCtx) {
    stop();
    callbacks.onError(new LiveCaptureError("unknown", "Canvas 2D bağlamı alınamadı"));
    return stoppedHandle();
  }

  callbacks.onStreamReady();

  intervalId = setInterval(() => {
    if (stopped) return;
    const vw = video.videoWidth;
    const vh = video.videoHeight;
    if (!vw || !vh) return;

    // imagePrep.ts'in downscale kuralıyla BİREBİR aynı ölçek: laplacianVariance
    // ölçeğe duyarlıdır, analiz karesi dosya yolundan farklı ölçekte çizilirse
    // SHARPNESS_MIN_VARIANCE eşiği canlı yolda başka bir şey ölçer.
    const longEdge = Math.min(Math.max(vw, vh), TARGET_MAX_EDGE_PX);
    const scale = longEdge / Math.max(vw, vh);
    const width = Math.round(vw * scale);
    const height = Math.round(vh * scale);
    analysisCanvas.width = width;
    analysisCanvas.height = height;
    analysisCtx.drawImage(video, 0, 0, width, height);

    const classification = classifier!.classify(analysisCanvas, performance.now());
    const sharpness = laplacianVariance(analysisCanvas);
    const luma = averageLuma(analysisCanvas);

    const evaluation = evaluateFrame({
      expected: slotId,
      classification,
      sharpness,
      luma,
      previousStreak: readyStreak,
    });
    readyStreak = evaluation.readyStreak;

    callbacks.onEvaluation(evaluation);

    if (readyStreak >= LIVE_REQUIRED_CONSECUTIVE) {
      // Döngüyü hemen durdur: prepareFromDrawable async'i sırasında interval
      // tekrar tetiklenip ikinci bir onCapture/prepareFromDrawable başlatmasın.
      if (intervalId !== undefined) {
        clearInterval(intervalId);
        intervalId = undefined;
      }
      // Ön kamerada (facingMode "user") ekranda ayna efekti gösteriliyor
      // (CSS transform, bkz. main.ts) — çekilen kare de bununla EŞLEŞMELİ,
      // yoksa kullanıcı ekranda gördüğünün tersini kaydedilmiş bulur (ör.
      // gömlekteki yazı ters çıkar). Yalnız ÖN kamerada, önce yatay çevrilmiş
      // bir canvas'a çizilip o kaynak gönderilir; arka kamerada dokunulmaz.
      const captureSource: CanvasImageSource =
        capture === "user" ? mirrorToCanvas(video, vw, vh) : video;
      prepareFromDrawable(captureSource, vw, vh)
        .then((prepared) => {
          if (stopped) {
            URL.revokeObjectURL(prepared.previewUrl);
            return;
          }
          stop();
          callbacks.onCapture(prepared, evaluation);
        })
        .catch((err) => {
          if (stopped) return;
          stop();
          callbacks.onError(new LiveCaptureError("unknown", `Kare hazırlanamadı: ${err instanceof Error ? err.message : String(err)}`));
        });
    }
  }, LIVE_CLASSIFY_INTERVAL_MS);

  return { stop };
}
